import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { motion } from "motion/react";
import { Quote, Star } from "lucide-react";
import { ALL_INDUSTRIES, INDUSTRY_DATA } from "../../data/industries";
function TestimonialsSection({ data, selectedId }) {
  const industry = ALL_INDUSTRIES.find((i) => i.id === selectedId);
  const testimonials = useMemo(() => [
    data,
    ...Object.values(INDUSTRY_DATA).filter((item) => item.id !== data.id)
  ], [data]);
  const midpoint = Math.ceil(testimonials.length / 2);
  const firstRow = testimonials.slice(0, midpoint);
  const secondRow = testimonials.slice(midpoint);
  const renderRow = (items, direction) => {
    const repeatedItems = [...items, ...items];
    return /* @__PURE__ */ jsx("div", { className: "testimonial-row relative overflow-hidden py-3", children: /* @__PURE__ */ jsx(
      "div",
      {
        className: "testimonial-track flex w-max gap-5",
        style: {
          animation: `${direction === "left" ? "testimonial-marquee-left" : "testimonial-marquee-right"} 34s linear infinite`
        },
        children: repeatedItems.map((item, index) => {
          const testimonial = item.testimonial;
          return /* @__PURE__ */ jsxs(
            "article",
            {
              className: "w-[320px] sm:w-[390px] shrink-0 rounded-3xl border bg-white p-5 sm:p-6",
              style: {
                borderColor: `${item.color}22`,
                boxShadow: "0 14px 42px rgba(15,23,42,0.07)",
                background: `linear-gradient(135deg,white 0%,${item.bgLight} 100%)`
              },
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4 mb-5", children: [
                  /* @__PURE__ */ jsx("div", { className: "flex gap-1 pt-1", children: [1, 2, 3, 4, 5].map((star) => /* @__PURE__ */ jsx(Star, { size: 15, fill: "#f59e0b", color: "#f59e0b" }, star)) }),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "w-9 h-9 rounded-2xl flex items-center justify-center shrink-0",
                      style: { backgroundColor: `${item.color}14`, color: item.color },
                      children: /* @__PURE__ */ jsx(Quote, { size: 17 })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs(
                  "blockquote",
                  {
                    className: "mb-6",
                    style: { fontFamily: "'Inter',sans-serif", fontSize: "0.96rem", lineHeight: 1.72, color: "#111827" },
                    children: [
                      '"',
                      testimonial.quote,
                      '"'
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
                  /* @__PURE__ */ jsx(
                    "img",
                    {
                      src: testimonial.avatar,
                      alt: testimonial.name,
                      className: "w-12 h-12 rounded-2xl object-cover border-2",
                      style: { borderColor: `${item.color}33` }
                    }
                  ),
                  /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
                    /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: "0.96rem", color: "#111827" }, children: testimonial.name }),
                    /* @__PURE__ */ jsx("div", { className: "truncate", style: { fontFamily: "'Inter',sans-serif", fontSize: "0.8rem", color: "#6b7280" }, children: testimonial.role }),
                    /* @__PURE__ */ jsx("div", { className: "truncate", style: { fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: "0.78rem", color: item.color, marginTop: "2px" }, children: testimonial.company })
                  ] })
                ] })
              ]
            },
            `${direction}-${item.id}-${index}`
          );
        })
      }
    ) });
  };
  return /* @__PURE__ */ jsxs("section", { className: "py-24 px-6 overflow-hidden", style: { backgroundColor: "#f8fafc" }, children: [
    /* @__PURE__ */ jsx("style", { children: `
        @keyframes testimonial-marquee-left {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }

        @keyframes testimonial-marquee-right {
          from { transform: translateX(-50%); }
          to { transform: translateX(0); }
        }

        .testimonial-marquee:hover .testimonial-track {
          animation-play-state: paused;
        }

        .testimonial-row::before,
        .testimonial-row::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          width: min(120px, 18vw);
          pointer-events: none;
          z-index: 2;
        }

        .testimonial-row::before {
          left: 0;
          background: linear-gradient(90deg,#f8fafc 0%,rgba(248,250,252,0) 100%);
        }

        .testimonial-row::after {
          right: 0;
          background: linear-gradient(270deg,#f8fafc 0%,rgba(248,250,252,0) 100%);
        }
      ` }),
    /* @__PURE__ */ jsxs("div", { className: "max-w-7xl mx-auto", children: [
      /* @__PURE__ */ jsxs("div", { className: "text-center mb-16", children: [
        /* @__PURE__ */ jsxs(
          motion.div,
          {
            initial: { opacity: 0, y: 14 },
            whileInView: { opacity: 1, y: 0 },
            viewport: { once: true },
            transition: { duration: 0.55 },
            className: "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-semibold",
            style: { backgroundColor: `${data.color}0d`, border: `1px solid ${data.color}25`, color: data.color, fontFamily: "'Manrope',sans-serif", letterSpacing: "0.06em" },
            children: [
              "TESTIMONIALS \xB7 ",
              industry?.label?.toUpperCase()
            ]
          },
          data.id
        ),
        /* @__PURE__ */ jsx(
          motion.h2,
          {
            initial: { opacity: 0, y: 20 },
            whileInView: { opacity: 1, y: 0 },
            viewport: { once: true },
            transition: { duration: 0.6, delay: 0.05 },
            style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(1.8rem,3.5vw,2.75rem)", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" },
            children: "What Our Clients Say"
          },
          `th-${data.id}`
        )
      ] }),
      /* @__PURE__ */ jsx("div", { className: "testimonial-marquee relative -mx-6 sm:-mx-10 lg:-mx-16", children: /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.65 },
          className: "space-y-4",
          children: [
            renderRow(firstRow, "left"),
            renderRow(secondRow, "right")
          ]
        }
      ) }),
      /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0, y: 16 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.6, delay: 0.2 },
          className: "mt-14 pt-10 border-t flex flex-wrap items-center justify-center gap-8",
          style: { borderColor: "#e5e7eb" },
          children: [
            /* @__PURE__ */ jsx("p", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.82rem", color: "#9ca3af", whiteSpace: "nowrap" }, children: "TRUSTED BY ENTERPRISES IN" }),
            ["Healthcare", "Transport", "Finance", "Retail", "Education", "Government", "Hospitality", "Manufacturing"].map((sector) => /* @__PURE__ */ jsx(
              "span",
              {
                className: "px-4 py-2 rounded-xl text-xs font-semibold",
                style: { fontFamily: "'Manrope',sans-serif", backgroundColor: "rgba(111,92,247,0.06)", color: "#6f5cf7", border: "1px solid rgba(111,92,247,0.12)" },
                children: sector
              },
              sector
            ))
          ]
        }
      )
    ] })
  ] });
}
export {
  TestimonialsSection
};
